<script setup lang="ts">
	import {
		computed,
		onBeforeUnmount,
		onMounted,
		reactive,
		ref,
		watch,
	} from 'vue'
	import { useRouter } from 'vue-router'
	import { useToast } from 'vue-toastification'

	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import EmailBlockList from '@/modules/companies/components/EmailBlockList.vue'
	import EmailLayoutEditor from '@/modules/companies/components/EmailLayoutEditor.vue'
	import type { Company } from '@/modules/companies/interfaces/company'
	import {
		type CompanyEmailSetting,
		type CompanyEmailSettingUpdateBody,
		type CompanyEmailVariantSetting,
		DEFAULT_EMAIL_LAYOUT,
		EMAIL_PREVIEW_OPTIONS,
		EMAIL_TYPE_OPTIONS,
		type EmailBlockState,
		type EmailLayout,
		type EmailPreviewRecipient,
		type EmailType,
		MAX_COPY_ADDRESSES,
		blocksNotIn,
		defaultEmailVariant,
	} from '@/modules/companies/interfaces/emailSettings'
	import PlanComparisonModal from '@/modules/plans/components/PlanComparisonModal.vue'
	import useFeatureLockCopy from '@/modules/plans/composables/useFeatureLockCopy'
	import usePlanFeature from '@/modules/plans/composables/usePlanFeature'

	import useCompanyEmailPreview from '../../../composables/useCompanyEmailPreview'
	import useCompanyEmailSettings from '../../../composables/useCompanyEmailSettings'

	interface Props {
		company: Company
		/**
		 * El panel lo abre la AGENCIA, de parte del cliente, desde el módulo interno.
		 *
		 * Cambia dos cosas y sólo dos: no aplica el candado del plan —las empresas sin el
		 * módulo son justo las que nadie puede apagar hoy— ni los permisos de rol del
		 * cliente, que son suyos y no nuestros. El backend hace el mismo corte, y es el que
		 * manda: ver CompanyEmailSettingsController::actingAsAgency().
		 */
		asAgency?: boolean
	}

	const props = withDefaults(defineProps<Props>(), { asAgency: false })

	const router = useRouter()
	const toast = useToast()
	const COMPANY = 'App\\Models\\Company'
	const { hasPermissionUser } = useHasPermissionUser()

	// En «Mi empresa» la pestaña sólo se monta si el usuario trae `view-emailSettings`
	// (MyCompany filtra TAB_DEFS por permiso), así que ahí sólo falta separar quién
	// escribe. En el módulo interno se monta sin permiso —la ve todo el personal de la
	// agencia— y `asAgency` es lo que decide que además pueda escribir.
	const canEdit = computed(
		() => props.asAgency || hasPermissionUser('update-emailSettings', COMPANY)
	)

	const companyId = computed(() => props.company.id)

	/**
	 * Las imágenes de la empresa para el editor de marcas.
	 *
	 * ⚠️ `company.logo` NUNCA viene vacío: la colección de media declara un `useFallbackUrl`
	 * a un placeholder genérico de Cloudinary, así que una empresa sin logo igual trae URL.
	 * Sin este filtro el editor diría «logo disponible», dejarías colocarlo, y el correo
	 * saldría sin nada —porque el backend sí distingue—. Se descarta por el nombre del
	 * archivo del placeholder, que es la única señal que llega al front.
	 */
	const PLACEHOLDER = 'default_company_logo'

	/**
	 * Los bloques viven dentro del layout, pero la lista los edita como arreglo suelto.
	 * Este puente evita que el componente tenga que conocer la forma completa del layout.
	 */
	const blockStates = computed<EmailBlockState[]>({
		get: () => {
			if (previewRecipient.value !== 'traveler')
				return variant.value.emailLayout.blocks

			// El bloque puede estar visible en el layout del viajero y aun así no salir: el
			// backend lo cruza con el permiso (`BLOCKS_OPT_IN`). Se enseña el permiso, que
			// es lo que de verdad decide.
			return variant.value.emailLayout.blocks.map((block) =>
				block.id === 'money' ?
					{ ...block, visible: form.showTravelerAmount }
				:	block
			)
		},
		set: (blocks) => {
			// En la versión del viajero, ese interruptor escribe el PERMISO —que es lo que
			// el backend consulta— y de paso deja el bloque en el mismo valor, para que no
			// quede un permiso encendido sobre un bloque oculto y el importe siga sin salir
			// sin que nada lo explique.
			if (previewRecipient.value === 'traveler') {
				const money = blocks.find((block) => block.id === 'money')
				if (money) form.showTravelerAmount = money.visible
			}

			variant.value.emailLayout = { ...variant.value.emailLayout, blocks }
		},
	})

	const companyLogoUrl = computed(() => {
		const url = props.company.logo
		return url && !url.includes(PLACEHOLDER) ? url : null
	})

	const companyIsotipoUrl = computed(() => {
		const url = props.company.isotipo
		return url && !url.includes(PLACEHOLDER) ? url : null
	})

	// Configurar correos es función de BUSINESS+; el backend responde 422 en los tres
	// endpoints. Con plan insuficiente el panel queda con blur + candado y el modal de
	// comparación, igual que grupos y políticas.
	const {
		locked: featureLocked,
		subscriptionId,
		currentPlanCode,
	} = usePlanFeature(companyId, 'email_settings')

	/** A la agencia no la gatea el plan del cliente. */
	const planLocked = computed(() => !props.asAgency && featureLocked.value)
	// El texto del candado sale del catálogo de features (columnas lock_*), no
	// cableado aquí: así nombra siempre los planes públicos que hoy la incluyen y no
	// se queda viejo si cambia la matriz o se publica un plan nuevo.
	const { lockTitle, lockDescription } = useFeatureLockCopy('email_settings')
	const showCompare = ref(false)
	const canManageBilling = computed(() => hasPermissionUser('update', COMPANY))

	// El swap y el cobro viven en la página de suscripción; el modal sólo elige plan.
	function onSelectPlan(planCode: string) {
		router.push({
			name: 'company.subscriptions',
			params: { companyId: String(companyId.value) },
			query: { planCode },
		})
	}

	const {
		settingFor,
		settings,
		isLoading,
		isError,
		isPaused,
		pausedNote,
		save,
		isSaving,
		sendTest,
		isSendingTest,
	} = useCompanyEmailSettings(companyId)

	const selectedType = ref<EmailType>('hotel_confirmation')

	const selectedOption = computed(
		() =>
			EMAIL_TYPE_OPTIONS.find((o) => o.value === selectedType.value) ??
			EMAIL_TYPE_OPTIONS[0]
	)

	/** El sobre y el diseño de UNA versión, tal como se editan en el formulario. */
	type VariantForm = {
		cc: string[]
		bcc: string[]
		replyTo: string
		senderName: string
		attachPdf: boolean
		showMapLink: boolean
		showPlatformBranding: boolean
		emailLayout: EmailLayout
	}

	function emptyVariantForm(): VariantForm {
		return {
			cc: [],
			bcc: [],
			replyTo: '',
			senderName: '',
			attachPdf: true,
			showMapLink: true,
			showPlatformBranding: true,
			emailLayout: structuredClone(DEFAULT_EMAIL_LAYOUT),
		}
	}

	/**
	 * Arriba lo que decide SI el correo sale; abajo, cómo sale cada versión.
	 *
	 * Los destinatarios NO se separan: son la misma decisión para el correo entero, y dos
	 * interruptores para lo mismo dejan al panel sin una respuesta clara.
	 */
	const form = reactive({
		enabled: true,
		notifyTraveler: true,
		notifyMember: true,
		notifySupervisor: true,
		showTravelerAmount: false,
		variants: {
			traveler: emptyVariantForm(),
			manager: emptyVariantForm(),
		} as Record<EmailPreviewRecipient, VariantForm>,
	})

	function syncVariant(
		recipient: EmailPreviewRecipient,
		target: VariantForm,
		source: CompanyEmailVariantSetting | undefined
	) {
		// El default de SU destinatario, no el del viajero: hoy da igual porque `recipient`
		// no se copia al formulario, pero un `'traveler'` literal en el camino del manager
		// es una trampa esperando a que alguien agregue un campo que sí dependa de cuál es.
		const values = source ?? defaultEmailVariant(recipient)

		target.cc = [...values.cc]
		target.bcc = [...values.bcc]
		target.replyTo = values.replyTo ?? ''
		target.senderName = values.senderName ?? ''
		target.attachPdf = values.attachPdf
		target.showMapLink = values.showMapLink
		target.showPlatformBranding = values.showPlatformBranding
		target.emailLayout =
			values.emailLayout ?? structuredClone(DEFAULT_EMAIL_LAYOUT)
	}

	function syncForm(setting: CompanyEmailSetting | null) {
		if (!setting) return
		form.enabled = setting.enabled
		form.notifyTraveler = setting.notifyTraveler
		form.notifyMember = setting.notifyMember
		form.notifySupervisor = setting.notifySupervisor
		form.showTravelerAmount = setting.showTravelerAmount ?? false

		EMAIL_PREVIEW_OPTIONS.forEach(({ value }) =>
			syncVariant(value, form.variants[value], setting.variants?.[value])
		)
	}

	// Se re-sincroniza al cambiar de tipo y al llegar/invalidarse la query.
	watch(
		[settings, selectedType],
		() => syncForm(settingFor(selectedType.value)),
		{ immediate: true }
	)

	/** `id` nulo = la empresa no ha guardado este tipo y está viendo los defaults. */
	const isConfigured = computed(
		() => (settingFor(selectedType.value)?.id ?? null) !== null
	)

	const RECIPIENTS = [
		{
			key: 'notifyTraveler' as const,
			label: 'El viajero',
			hint: 'Quien viaja recibe su propia versión del correo.',
		},
		{
			key: 'notifyMember' as const,
			label: 'Quien solicita',
			hint: 'El usuario que hizo la reserva, si no es el viajero.',
		},
		{
			key: 'notifySupervisor' as const,
			label: 'El supervisor',
			hint: 'El supervisor asignado al viajero.',
		},
	]

	const hasRecipient = computed(
		() => form.notifyTraveler || form.notifyMember || form.notifySupervisor
	)

	// Mismo criterio que el backend: encendido y sin destinatarios no envía nada, ni a
	// los CC (las copias acompañan al correo de un destinatario, no salen solas).
	const recipientError = computed(() =>
		form.enabled && !hasRecipient.value ?
			'Selecciona al menos un destinatario, o apaga el correo. Las copias no se envían solas.'
		:	null
	)

	const disabled = computed(() => !canEdit.value || planLocked.value)

	/** Los interruptores del correo entero: si sale y a quién. */
	function toggle(
		key: 'enabled' | 'showTravelerAmount' | (typeof RECIPIENTS)[number]['key']
	) {
		if (disabled.value) return
		form[key] = !form[key]
	}

	/** Y los de la versión que se está editando, que ya no son los mismos. */
	function toggleVariant(
		key: 'attachPdf' | 'showMapLink' | 'showPlatformBranding'
	) {
		if (disabled.value) return
		variant.value[key] = !variant.value[key]
	}

	function trackClass(on: boolean) {
		return [
			'relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none',
			on ? 'bg-[#021F58]' : 'bg-gray-200',
			disabled.value ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
		]
	}

	function knobClass(on: boolean) {
		return [
			'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
			on ? 'translate-x-4' : 'translate-x-0',
		]
	}

	function addAddress(list: 'cc' | 'bcc') {
		if (disabled.value) return
		if (variant.value[list].length >= MAX_COPY_ADDRESSES) return
		variant.value[list].push('')
	}

	function removeAddress(list: 'cc' | 'bcc', index: number) {
		if (disabled.value) return
		variant.value[list].splice(index, 1)
	}

	const inputClass =
		'w-full rounded-xl border border-gray-200 text-sm text-gray-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 focus:border-[#021F58] disabled:bg-gray-50 disabled:text-gray-400'

	/** Descarta las filas vacías que deja el botón de "Agregar" sin llenar. */
	function cleanList(list: string[]) {
		return list.map((a) => a.trim()).filter((a) => a.length > 0)
	}

	// -------------------------------------------------------------------------
	// Vista previa
	// -------------------------------------------------------------------------

	const previewRecipient = ref<EmailPreviewRecipient>('traveler')

	/**
	 * La versión que se está editando, que es LA MISMA que se previsualiza.
	 *
	 * Un solo control para las dos cosas y no dos: editar una versión mientras se mira la
	 * otra es la forma más rápida de guardar copias en el correo equivocado, que es
	 * justamente lo que este cambio vino a hacer imposible.
	 */
	const variant = computed(() => form.variants[previewRecipient.value])

	/** Cómo se llama la versión que se está editando, para nombrarla en los textos. */
	const editedVariantLabel = computed(
		() =>
			EMAIL_PREVIEW_OPTIONS.find(
				(o) => o.value === previewRecipient.value
			)?.label.toLowerCase() ?? ''
	)

	const {
		html: previewHtml,
		isLoading: isPreviewLoading,
		isError: isPreviewError,
	} = useCompanyEmailPreview(
		companyId,
		selectedType,
		previewRecipient,
		computed(() => variant.value.showMapLink),
		computed(() => variant.value.showPlatformBranding),
		computed(() => form.showTravelerAmount),
		computed(() => variant.value.emailLayout),
		// Con plan insuficiente el backend responde 422: no se pide y el panel queda con
		// el candado encima.
		computed(() => !planLocked.value)
	)

	/**
	 * El iframe se ajusta al alto REAL del correo para que la previa se vea completa y a
	 * escala, sin scroll interno.
	 *
	 * Se puede medir porque el sandbox lleva `allow-same-origin` (y NO `allow-scripts`):
	 * nada se ejecuta dentro, pero el documento queda accesible al padre. El try/catch está
	 * porque el acceso puede lanzar si el navegador decide tratarlo como otro origen, y ahí
	 * se prefiere el alto de respaldo antes que reventar la pestaña.
	 */
	const previewFrame = ref<HTMLIFrameElement | null>(null)
	const FALLBACK_HEIGHT = 720
	const previewHeight = ref(FALLBACK_HEIGHT)

	function fitPreviewHeight() {
		try {
			const doc = previewFrame.value?.contentDocument
			// scrollHeight del <html>: el <body> del correo no siempre contiene el alto real
			// porque las tablas se desbordan del flujo normal.
			const measured = doc?.documentElement?.scrollHeight ?? 0
			previewHeight.value = measured > 0 ? measured : FALLBACK_HEIGHT
		} catch {
			previewHeight.value = FALLBACK_HEIGHT
		}
	}

	// El correo es responsivo: al cambiar el ancho cambia su alto y hay que volver a medir.
	onMounted(() => window.addEventListener('resize', fitPreviewHeight))
	onBeforeUnmount(() => window.removeEventListener('resize', fitPreviewHeight))

	/**
	 * Los datos del SOBRE se pintan con el estado vivo del formulario, no con lo que
	 * devuelve el endpoint: así el usuario ve cambiar el remitente y las copias al mover un
	 * switch, sin tener que guardar primero. El cuerpo del correo (el iframe) sí viene del
	 * backend, porque depende del logo de la empresa y del blade real.
	 */
	const envelope = computed(() => ({
		from: variant.value.senderName.trim() || 'Enlacebooking',
		replyTo: variant.value.replyTo.trim(),
		cc: cleanList(variant.value.cc),
		bcc: cleanList(variant.value.bcc),
		attachPdf: variant.value.attachPdf,
	}))

	/**
	 * La primera versión con una dirección mal escrita, o null si están todas bien.
	 *
	 * Sin esto el backend responde `El campo variants.manager.cc.0 no es un correo válido`
	 * y el toast lo pinta crudo: el usuario está editando «Viajero», el error habla de
	 * `manager`, y el campo ni siquiera está en pantalla. Antes de separar las versiones no
	 * podía pasar —todas las copias se veían a la vez—, así que lo trae esta pestaña y le
	 * toca resolverlo.
	 */
	function firstInvalidVariant(): EmailPreviewRecipient | null {
		const looksLikeEmail = (value: string) =>
			/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

		return (
			EMAIL_PREVIEW_OPTIONS.map(({ value }) => value).find((recipient) =>
				[
					...cleanList(form.variants[recipient].cc),
					...cleanList(form.variants[recipient].bcc),
					...(form.variants[recipient].replyTo.trim() ?
						[form.variants[recipient].replyTo.trim()]
					:	[]),
				].some((address) => !looksLikeEmail(address))
			) ?? null
		)
	}

	function submit() {
		if (disabled.value || recipientError.value) return

		// Se salta a la versión que falló antes de avisar: un error sobre un campo que no
		// está en pantalla no es un error, es un acertijo.
		const invalid = firstInvalidVariant()

		if (invalid) {
			previewRecipient.value = invalid
			toast.error(
				`Revisa las direcciones de la versión «${
					EMAIL_PREVIEW_OPTIONS.find((o) => o.value === invalid)?.label ?? ''
				}»: alguna no parece un correo.`
			)
			return
		}

		save({
			emailType: selectedType.value,
			enabled: form.enabled,
			notifyTraveler: form.notifyTraveler,
			notifyMember: form.notifyMember,
			notifySupervisor: form.notifySupervisor,
			showTravelerAmount: form.showTravelerAmount,

			// Las DOS versiones en cada guardado, no sólo la que está en pantalla: el
			// backend las exige juntas para que guardar sea una foto completa y no un
			// parche del que nadie sabe qué quedó fuera.
			variants: Object.fromEntries(
				EMAIL_PREVIEW_OPTIONS.map(({ value }) => {
					const source = form.variants[value]

					return [
						value,
						{
							recipient: value,
							cc: cleanList(source.cc),
							bcc: cleanList(source.bcc),
							replyTo: source.replyTo.trim() || null,
							senderName: source.senderName.trim() || null,
							attachPdf: source.attachPdf,
							showMapLink: source.showMapLink,
							showPlatformBranding: source.showPlatformBranding,
							emailLayout: source.emailLayout,
						},
					]
				})
			) as CompanyEmailSettingUpdateBody['variants'],
		})
	}
</script>

<template>
	<div class="mt-4">
		<!-- Encabezado -->
		<div class="mb-4">
			<h2 class="flex items-center gap-2 text-lg font-bold text-gray-900">
				<span class="material-symbols-outlined text-[20px]">mail</span>
				Configuración de correos
			</h2>
			<p class="text-sm text-gray-500">
				Decide qué correos de confirmación salen, a quién y con qué copias.
			</p>
		</div>

		<!-- Plan sin la función (free/start/pro): blur + overlay de upsell -->
		<div :class="planLocked ? 'relative min-h-[420px]' : ''">
			<div
				v-if="planLocked"
				class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-xl bg-white/60 p-8 text-center backdrop-blur-[6px]"
			>
				<span class="material-symbols-outlined text-5xl text-gray-500"
					>lock</span
				>
				<h3 class="max-w-lg text-xl font-bold text-gray-900">
					{{ lockTitle }}
				</h3>
				<p
					v-if="lockDescription"
					class="max-w-md text-sm text-gray-600"
				>
					{{ lockDescription }}
				</p>
				<button
					class="mt-2 rounded-lg bg-[#021F58] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
					@click="showCompare = true"
				>
					Ver planes
				</button>
			</div>

			<div
				:class="planLocked ? 'pointer-events-none select-none' : ''"
				:aria-hidden="planLocked"
			>
				<!-- Cargando -->
				<div
					v-if="isLoading"
					class="animate-pulse space-y-3"
				>
					<div class="h-10 w-72 rounded-xl bg-gray-100" />
					<div class="h-96 rounded-2xl bg-gray-100" />
				</div>

				<!--
					Feature pausada: estado esperado, no error.
					Pintarlo en rojo como "no se pudo cargar" hace que el cliente crea que
					algo se rompió y lo reporte. Y tampoco va el anuncio de upgrade: no es
					un tema de plan, y ofrecerle pagar por algo que no va a poder usar
					hasta que se reactive sería mentira.
				-->
				<div
					v-else-if="isPaused"
					class="flex flex-col items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-6 py-12 text-center"
				>
					<span class="material-symbols-outlined text-4xl text-gray-400">
						construction
					</span>
					<h3 class="text-base font-semibold text-gray-900">
						Estamos trabajando en esta función
					</h3>
					<p class="max-w-md text-sm text-gray-500">
						{{ pausedNote }}
					</p>
					<p class="text-xs text-gray-400">
						No necesitas hacer nada. La habilitaremos en cuanto esté lista.
					</p>
				</div>

				<!-- Error de carga -->
				<div
					v-else-if="isError && !planLocked"
					class="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700"
				>
					No se pudo cargar la configuración de correos.
				</div>

				<template v-else>
					<!-- Selector de tipo de correo -->
					<div class="mb-4 flex flex-wrap gap-2">
						<button
							v-for="opt in EMAIL_TYPE_OPTIONS"
							:key="opt.value"
							type="button"
							:class="[
								'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-all duration-150 focus:outline-none',
								selectedType === opt.value ?
									'bg-[#253C70] text-white shadow-sm'
								:	'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-800',
							]"
							@click="selectedType = opt.value"
						>
							<span class="material-symbols-outlined text-[17px]">{{
								opt.icon
							}}</span>
							{{ opt.label }}
						</button>
					</div>

					<!-- Dos columnas: a la izquierda lo que se acomoda, a la derecha el correo
					     real. Se apilan abajo de 2xl, no de xl: en el módulo interno el panel
					     comparte el ancho con el sidebar (~256px), la columna de pestañas y su
					     padding, así que a 1280 y 1366 la tarjeta de configuración quedaba en
					     108 y 194px y el interruptor «Activo» —el que apaga el correo, que es
					     para lo que existe esta pestaña— se recortaba fuera de la tarjeta.

					     Y `grid-cols-1` explícito abajo de ese corte: sin él la pista implícita
					     es `auto` y crece al min-content del contenido. Las filas de bloques
					     usan `truncate` (nowrap), cuyo min-content es la línea entera, así que
					     el truncate nunca actuaba y en móvil la página se desbordaba. -->
					<div
						class="grid grid-cols-1 gap-6 2xl:grid-cols-[minmax(0,1fr)_minmax(0,560px)] 2xl:items-start"
					>
						<div>
							<div
								class="overflow-hidden rounded-2xl border border-gray-200 bg-white"
							>
								<!-- Cabecera de la tarjeta: el interruptor maestro -->
								<div
									class="flex items-center justify-between gap-4 border-b border-gray-100 px-5 py-4"
								>
									<div class="flex items-center gap-3">
										<div
											class="flex h-9 w-9 items-center justify-center rounded-xl bg-[#021F58]"
										>
											<span
												class="material-symbols-outlined text-[18px] text-white"
												>{{ selectedOption.icon }}</span
											>
										</div>
										<div>
											<p class="text-sm font-semibold text-gray-900">
												{{ selectedOption.label }}
											</p>
											<p class="text-xs text-gray-400">
												{{ selectedOption.description }}
											</p>
										</div>
									</div>

									<div class="flex items-center gap-2.5">
										<span class="text-xs text-gray-500">
											{{ form.enabled ? 'Activo' : 'Apagado' }}
										</span>
										<button
											type="button"
											:disabled="disabled"
											role="switch"
											:aria-checked="form.enabled"
											aria-label="Enviar este correo"
											:class="trackClass(form.enabled)"
											@click="toggle('enabled')"
										>
											<span :class="knobClass(form.enabled)" />
										</button>
									</div>
								</div>

								<!-- Aviso de que aún no se ha guardado nada -->
								<div
									v-if="!isConfigured"
									class="border-b border-gray-100 bg-gray-50 px-5 py-3 text-xs text-gray-500"
								>
									Esta empresa todavía no tiene una configuración guardada.
									Estás viendo los valores por omisión, que son los que se
									aplican hoy.
								</div>

								<div class="space-y-6 px-5 py-5">
									<!-- Destinatarios -->
									<section>
										<h3
											class="text-xs font-bold uppercase tracking-wider text-gray-500"
										>
											Destinatarios
										</h3>
										<div class="mt-3 space-y-2">
											<div
												v-for="row in RECIPIENTS"
												:key="row.key"
												class="flex items-center justify-between gap-4 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3"
											>
												<div>
													<p class="text-sm font-medium text-gray-900">
														{{ row.label }}
													</p>
													<p class="text-xs text-gray-400">{{ row.hint }}</p>
												</div>
												<button
													type="button"
													:disabled="disabled"
													role="switch"
													:aria-checked="form[row.key]"
													:aria-label="row.label"
													:class="trackClass(form[row.key])"
													@click="toggle(row.key)"
												>
													<span :class="knobClass(form[row.key])" />
												</button>
											</div>
										</div>
										<p
											v-if="recipientError"
											class="mt-2 text-sm text-red-600"
										>
											{{ recipientError }}
										</p>
									</section>

									<!--
										De aquí para abajo, todo es de UNA versión del correo.

										La pestaña es la misma que manda la vista previa a propósito:
										se edita lo que se está viendo. Con dos controles separados,
										capturar copias en la versión equivocada era cuestión de
										tiempo, y ese error no se nota hasta que alguien no recibe
										su correo.
									-->
									<section
										class="rounded-xl border border-[#253C70]/20 bg-[#253C70]/[0.04] p-3"
									>
										<h3
											class="text-xs font-bold uppercase tracking-wider text-gray-500"
										>
											Estás editando
										</h3>
										<div class="mt-2 flex flex-wrap gap-1.5">
											<button
												v-for="opt in EMAIL_PREVIEW_OPTIONS"
												:key="opt.value"
												type="button"
												:class="[
													'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
													previewRecipient === opt.value ?
														'bg-[#253C70] text-white'
													:	'bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-800',
												]"
												@click="previewRecipient = opt.value"
											>
												{{ opt.label }}
											</button>
										</div>
										<p class="mt-2 text-xs text-gray-500">
											Las copias, el remitente y el diseño se guardan por
											separado para cada versión. Lo de arriba —si el correo
											sale y a quién— es el mismo para las dos.
										</p>
									</section>

									<!-- Copias -->
									<section>
										<h3
											class="text-xs font-bold uppercase tracking-wider text-gray-500"
										>
											Copias
										</h3>
										<p class="mt-1 text-xs text-gray-400">
											Buzones de la empresa que reciben cada confirmación de
											<strong>{{ editedVariantLabel }}</strong
											>. Las copias acompañan al correo de los destinatarios de
											arriba.
										</p>

										<div class="mt-3 grid grid-cols-1 gap-5 sm:grid-cols-2">
											<div
												v-for="list in ['cc', 'bcc'] as const"
												:key="list"
											>
												<div class="mb-2 flex items-center justify-between">
													<label
														class="text-xs font-medium uppercase text-gray-500"
													>
														{{
															list === 'cc' ? 'Copia (CC)' : (
																'Copia oculta (BCC)'
															)
														}}
													</label>
													<button
														v-if="!disabled"
														type="button"
														:disabled="
															variant[list].length >= MAX_COPY_ADDRESSES
														"
														class="inline-flex items-center gap-1 text-xs font-medium text-[#021F58] hover:underline disabled:cursor-not-allowed disabled:text-gray-300 disabled:no-underline"
														@click="addAddress(list)"
													>
														<span class="material-symbols-outlined text-[16px]"
															>add</span
														>
														Agregar
													</button>
												</div>

												<p
													v-if="!variant[list].length"
													class="rounded-xl border border-dashed border-gray-200 px-3 py-3 text-xs text-gray-400"
												>
													Sin direcciones.
												</p>

												<div
													v-else
													class="space-y-2"
												>
													<div
														v-for="(_, index) in variant[list]"
														:key="index"
														class="flex items-center gap-2"
													>
														<input
															v-model="variant[list][index]"
															type="email"
															:disabled="disabled"
															placeholder="viajes@empresa.com"
															:class="inputClass"
														/>
														<button
															v-if="!disabled"
															type="button"
															class="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
															:aria-label="`Quitar dirección ${index + 1}`"
															@click="removeAddress(list, index)"
														>
															<span
																class="material-symbols-outlined text-[18px]"
																>delete</span
															>
														</button>
													</div>
												</div>
											</div>
										</div>
									</section>

									<!-- Remitente -->
									<section>
										<h3
											class="text-xs font-bold uppercase tracking-wider text-gray-500"
										>
											Remitente
										</h3>
										<div class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
											<div>
												<label
													class="mb-1.5 block text-xs font-medium text-gray-500"
												>
													Nombre visible
												</label>
												<input
													v-model="variant.senderName"
													type="text"
													maxlength="100"
													:disabled="disabled"
													placeholder="Viajes de tu empresa"
													:class="inputClass"
												/>
												<p class="mt-1 text-xs text-gray-400">
													El nombre que aparece como remitente. La dirección de
													envío sigue siendo la de la plataforma.
												</p>
											</div>
											<div>
												<label
													class="mb-1.5 block text-xs font-medium text-gray-500"
												>
													Correo de respuesta
												</label>
												<input
													v-model="variant.replyTo"
													type="email"
													:disabled="disabled"
													placeholder="viajes@empresa.com"
													:class="inputClass"
												/>
												<p class="mt-1 text-xs text-gray-400">
													A dónde llegan las respuestas de quien recibe el
													correo.
												</p>
											</div>
										</div>
									</section>

									<!-- Contenido: sólo hotel, los correos de vuelo no traen mapa -->
									<section v-if="selectedType === 'hotel_confirmation'">
										<h3
											class="text-xs font-bold uppercase tracking-wider text-gray-500"
										>
											Contenido
										</h3>
										<div
											class="mt-3 flex items-center justify-between gap-4 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3"
										>
											<div>
												<p class="text-sm font-medium text-gray-900">
													Mostrar la ubicación en Google Maps
												</p>
												<p class="text-xs text-gray-400">
													Agrega un botón al mapa del hospedaje, en el correo y
													en el PDF. La dirección se sigue mostrando.
												</p>
											</div>
											<button
												type="button"
												:disabled="disabled"
												role="switch"
												:aria-checked="variant.showMapLink"
												aria-label="Mostrar la ubicación en Google Maps"
												:class="trackClass(variant.showMapLink)"
												@click="toggleVariant('showMapLink')"
											>
												<span :class="knobClass(variant.showMapLink)" />
											</button>
										</div>
									</section>

									<!-- Nuestra marca: aplica a TODOS los tipos, no sólo hotel -->
									<section>
										<h3
											class="text-xs font-bold uppercase tracking-wider text-gray-500"
										>
											Marca
										</h3>
										<div
											class="mt-3 flex items-center justify-between gap-4 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3"
										>
											<div>
												<p class="text-sm font-medium text-gray-900">
													Mostrar los datos de Enlacebooking
												</p>
												<p class="text-xs text-gray-400">
													Nuestro soporte, teléfono y sitio al pie del correo y
													del PDF. Apágalo si la empresa presenta la
													confirmación como propia. Su logo no se toca.
												</p>
											</div>
											<button
												type="button"
												:disabled="disabled"
												role="switch"
												:aria-checked="variant.showPlatformBranding"
												aria-label="Mostrar la marca de Enlacebooking"
												:class="trackClass(variant.showPlatformBranding)"
												@click="toggleVariant('showPlatformBranding')"
											>
												<span
													:class="knobClass(variant.showPlatformBranding)"
												/>
											</button>
										</div>
									</section>

									<!-- Marcas de la empresa: plantilla y posición -->
									<section>
										<h3
											class="text-xs font-bold uppercase tracking-wider text-gray-500"
										>
											Diseño y marca de la empresa
										</h3>
										<div class="mt-3">
											<EmailLayoutEditor
												v-model="variant.emailLayout"
												:disabled="disabled"
												:logo-url="companyLogoUrl"
												:isotipo-url="companyIsotipoUrl"
											/>

											<p
												class="mt-5 text-xs font-semibold uppercase tracking-wide text-gray-400"
											>
												Orden de los bloques
											</p>
											<p class="mb-2 mt-0.5 text-xs text-gray-400">
												Arrástralos para reordenarlos. La vista previa se
												actualiza al soltar.
											</p>
											<EmailBlockList
												v-model="blockStates"
												:disabled="disabled"
												:unavailable="blocksNotIn(previewRecipient)"
											/>
										</div>
									</section>

									<!-- Adjuntos -->
									<section>
										<h3
											class="text-xs font-bold uppercase tracking-wider text-gray-500"
										>
											Adjuntos
										</h3>
										<div
											class="mt-3 flex items-center justify-between gap-4 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3"
										>
											<div>
												<p class="text-sm font-medium text-gray-900">
													Adjuntar el PDF de confirmación
												</p>
												<p class="text-xs text-gray-400">
													Apágalo si prefieres que el correo llegue sin archivo.
												</p>
											</div>
											<button
												type="button"
												:disabled="disabled"
												role="switch"
												:aria-checked="variant.attachPdf"
												aria-label="Adjuntar el PDF de confirmación"
												:class="trackClass(variant.attachPdf)"
												@click="toggleVariant('attachPdf')"
											>
												<span :class="knobClass(variant.attachPdf)" />
											</button>
										</div>
									</section>
								</div>

								<!-- Barra de guardado -->
								<div
									v-if="canEdit && !planLocked"
									class="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 px-5 py-3"
								>
									<button
										type="button"
										:disabled="isSendingTest"
										class="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
										@click="
											sendTest({
												emailType: selectedType,
												recipient: previewRecipient,
											})
										"
									>
										<span class="material-symbols-outlined text-[18px]"
											>send</span
										>
										{{ isSendingTest ? 'Enviando…' : 'Enviar prueba' }}
									</button>
									<button
										type="button"
										:disabled="isSaving || !!recipientError"
										class="inline-flex items-center gap-1.5 rounded-xl bg-[#021F58] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1C3A7A] disabled:cursor-not-allowed disabled:opacity-50"
										@click="submit"
									>
										{{ isSaving ? 'Guardando…' : 'Guardar' }}
									</button>
								</div>
								<div
									v-else-if="!canEdit"
									class="border-t border-gray-100 px-5 py-3 text-xs text-gray-400"
								>
									No tienes permiso para editar esta configuración. Pídeselo al
									Master de tu empresa.
								</div>
							</div>

							<p class="mt-3 text-xs text-gray-400">
								La prueba se envía únicamente a tu correo; las copias
								configuradas no la reciben. A quien no tenga un correo dado de
								alta en sus notificaciones no se le envía, aunque su interruptor
								esté encendido.
							</p>
						</div>

						<!-- Vista previa: el blade real, con el logo que de verdad va a salir.
						     Pegada arriba en pantallas anchas para que no se pierda al desplazar
						     la columna de controles, que es más larga. -->
						<section class="2xl:sticky 2xl:top-4">
							<div
								class="overflow-hidden rounded-2xl border border-gray-200 bg-white"
							>
								<div
									class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3"
								>
									<p
										class="text-xs font-bold uppercase tracking-wider text-gray-500"
									>
										Vista previa
									</p>
									<!--
										Estos botones y los de «Estás editando» escriben el MISMO
										estado: no son dos controles, son dos accesos al mismo. Sin
										el título, éste parecía decidir sólo qué se ve, y de hecho
										decide también qué se está editando.
									-->
									<div class="flex flex-wrap gap-1.5">
										<button
											v-for="opt in EMAIL_PREVIEW_OPTIONS"
											:key="opt.value"
											type="button"
											:class="[
												'rounded-lg px-2.5 py-1 text-xs font-medium transition-colors',
												previewRecipient === opt.value ?
													'bg-[#253C70] text-white'
												:	'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-800',
											]"
											:title="`Ver y editar la versión: ${opt.label}`"
											@click="previewRecipient = opt.value"
										>
											{{ opt.label }}
										</button>
									</div>
								</div>

								<!-- El sobre: sale del formulario en vivo, cambia sin guardar -->
								<dl
									class="space-y-1.5 border-b border-gray-100 bg-gray-50/60 px-4 py-3 text-xs"
								>
									<div class="flex gap-2">
										<dt class="w-20 shrink-0 text-gray-400">De</dt>
										<dd class="truncate font-medium text-gray-700">
											{{ envelope.from }}
										</dd>
									</div>
									<div class="flex gap-2">
										<dt class="w-20 shrink-0 text-gray-400">Responder a</dt>
										<dd class="truncate text-gray-700">
											{{ envelope.replyTo || 'Sin configurar' }}
										</dd>
									</div>
									<div
										v-if="envelope.cc.length"
										class="flex gap-2"
									>
										<dt class="w-20 shrink-0 text-gray-400">CC</dt>
										<dd class="truncate text-gray-700">
											{{ envelope.cc.join(', ') }}
										</dd>
									</div>
									<div
										v-if="envelope.bcc.length"
										class="flex gap-2"
									>
										<dt class="w-20 shrink-0 text-gray-400">BCC</dt>
										<dd class="truncate text-gray-700">
											{{ envelope.bcc.join(', ') }}
										</dd>
									</div>
									<div class="flex gap-2">
										<dt class="w-20 shrink-0 text-gray-400">Adjunto</dt>
										<dd class="text-gray-700">
											{{
												envelope.attachPdf ?
													'PDF de confirmación'
												:	'Sin adjunto'
											}}
										</dd>
									</div>
								</dl>

								<div
									v-if="isPreviewLoading"
									class="h-[720px] animate-pulse bg-gray-100"
								/>
								<div
									v-else-if="isPreviewError"
									class="flex h-[320px] items-center justify-center px-4 py-8 text-center text-xs text-gray-400"
								>
									No se pudo cargar la vista previa.
								</div>
								<!-- srcdoc y no src: el iframe hacia el API sería cross-site y la
							     cookie de sesión no viajaría.
							     sandbox con allow-same-origin y SIN allow-scripts: nada se
							     ejecuta dentro, pero el padre sí puede medir el documento para
							     darle al iframe el alto real del correo. Sin eso quedaba un
							     recorte con scroll interno en vez de una previa completa. -->
								<iframe
									v-else
									ref="previewFrame"
									:srcdoc="previewHtml"
									sandbox="allow-same-origin"
									title="Vista previa del correo de confirmación"
									class="w-full border-0 bg-white transition-[height] duration-200"
									:style="{ height: previewHeight + 'px' }"
									@load="fitPreviewHeight"
								/>
							</div>

							<p class="mt-3 text-xs text-gray-400">
								El correo completo, a escala real y con datos de ejemplo. El
								logo es el de tu empresa si subiste uno en Información general;
								si no, el de Enlacebooking.
							</p>
						</section>
					</div>
				</template>
			</div>

			<PlanComparisonModal
				v-model:open="showCompare"
				:company-id="companyId"
				:subscription-id="subscriptionId"
				:current-plan-code="currentPlanCode"
				:can-manage="canManageBilling"
				@select="onSelectPlan"
			/>
		</div>
	</div>
</template>
